// Client for the visual/dashboard DATA API (/api/visual-data).
// Turns a saved or inline-edited visual model into a renderable dataset.

import api from './client'
import type {
  VisualDataPayload, VisualModelPayload, VisualPlan,
} from '@twasta/visual-engine'
import type {
  CriteriaIssue, ItemChangeResult, LookupResponse, TxnNotifier, VisualCriteriaRuntime,
} from '@twasta/page-runtime/txn'

/**
 * The reserved prompt name a VIEWER's own row limit travels on.
 *
 * A reserved param rather than a new request field because every surface that
 * can ask for more rows already puts a params dict on the wire — the preview
 * modal, the dashboard tile, the smart-page embed and the runtime client — and
 * a new field would have to be threaded through each of them and both servers.
 * The three servers read the same name.
 */
export const ROW_LIMIT_PARAM = '__row_limit'

export interface VisualDataRequest {
  project: string
  /** Saved visual object name. Omit when passing inline_model. */
  obj_name?: string
  /** Unsaved VisualModel JSON to preview without saving first. */
  inline_model?: unknown
  /**
   * The obj_name the inline_model was loaded for. Lets the server discard a
   * stale inline model that belongs to a previously-open visual (preview race).
   */
  inline_model_for?: string
  /** Prompt-variable values, keyed by variable name (e.g. { REGION: 'EAST' }). */
  params?: Record<string, unknown>
}

/** Fetch the [schemaRow, ...rows] dataset + layout for one visual. */
export async function fetchVisualData(
  body: VisualDataRequest,
): Promise<VisualDataPayload> {
  const res = await api.post('/visual-data/visual', body)
  return res.data as VisualDataPayload
}

export interface UnderlyingDataResponse {
  success: boolean
  jsonData: VisualDataPayload['jsonData']
  rowCount: number
  truncated: boolean
  limit: number
  offset: number
  /** 'detail' for sqlmodel visuals, 'result' for raw-SQL visuals (degraded). */
  grain: string
}

/**
 * Fetch the row-level detail rows behind a clicked visual point
 * (drill-through underlying data). `filters` are RAW jsonData values keyed by
 * column NAME; the server validates the keys and rejects unknown ones.
 */
export async function fetchUnderlyingData(body: {
  project: string
  obj_name?: string
  inline_model?: unknown
  params?: Record<string, unknown>
  filters: Record<string, unknown>
  limit?: number
  offset?: number
}): Promise<UnderlyingDataResponse> {
  const res = await api.post('/visual-data/underlying', body)
  return res.data as UnderlyingDataResponse
}

export interface DashboardTile {
  VISUAL_ID: string
  VISUAL_NAME?: string
  /** The tile's title bar (absent = shown). */
  SHOW_HEADER?: boolean
  POSITION?: { X: number; Y: number; WIDTH: number; HEIGHT: number }
  jsonData?: VisualDataPayload['jsonData']
  visualLayout?: VisualDataPayload['visualLayout']
  optionsArr?: VisualDataPayload['optionsArr']
  rowCount?: number
  error?: string
  // A prompt this tile requires has no value (not even a default) — opens
  // the criteria bar (dashboardCriteriaNeedInput).
  needsInput?: boolean
  // Resolved visual obj name + its resolved params — so a tile can fetch its
  // own underlying data via /visual-data/underlying (Phase 7).
  VISUAL_OBJ?: string
  resolvedParams?: Record<string, unknown>
  // Report tile (TILE_KIND 'report'): the client mounts the shared
  // ReportViewer against /report-data/* with the server-resolved bound params.
  TILE_KIND?: string
  REPORT_OBJ?: string
  reportParams?: Record<string, unknown>
  // In-memory model path: this tile was NOT queried — it folds its dataset out
  // of the shared model named by `plan.sourceKey`. `plan.supported === false`
  // (with a `reason`) means the opposite: it was queried live, as always.
  modelBacked?: boolean
  plan?: VisualPlan
  // This tile's own query hit the row ceiling, so it holds the first N rows of
  // a larger result. It therefore declines the dashboard's quick filter (and
  // says so) rather than filtering a slice and reporting a partial total.
  truncated?: boolean
  // What that limit WAS, what this instance allows, and whether the limit is
  // the author's Top-N (rather than the ceiling) or one they pinned — the
  // badge says a different thing in each case.
  rowLimit?: number | null
  rowCeiling?: number | null
  rowLimitAuthored?: boolean
  rowLimitFixed?: boolean
}

export interface DashboardDataResult {
  success: boolean
  /** Shared in-memory datasets, one per data source (in_memory requests only). */
  models?: VisualModelPayload[]
  dashboard: {
    DASHBOARD_ID?: string
    DASHBOARD_NAME?: string
    DASHBOARD_DESCR?: string
    REFRESH_INTERVAL_SECONDS?: number
    /** 'default' (the application's colour), a theme-colour id, or '#rrggbb'. */
    THEME_COLOR?: string
    GLOBAL_CRITERIA: Array<{
      PARAM_NAME: string
      PROMPT_LABEL?: string
      DEFAULT_VALUE?: string
      DATA_TYPE?: string
      OPERATOR?: string
      BROADCAST_TO?: string[]
      // F2 lookup + on-change item_change + options picklist — parity with the
      // single-visual criteria so a dashboard global can use them too.
      lookup?: string
      lookup_filter?: string
      lookup_filter_rules?: unknown
      lookup_sql?: string
      lookup_title?: string
      lookup_sql_data_col?: string
      item_change?: unknown
      options?: Array<{ value: string; label?: string }> | string[]
      mandatory?: boolean | string
    }>
    DRILL_THROUGH: unknown[]
    // Server-resolved global-criteria values (token defaults resolved to
    // concrete values), keyed by PARAM_NAME — seeds the criteria inputs.
    resolvedGlobals?: Record<string, string>
  }
  tiles: DashboardTile[]
  /** The dashboard's criteria were refused (mandatory / validations): no tile
   *  ran, and `criteriaErrors` says why per criterion. */
  needsInput?: boolean
  missingParams?: string[]
  criteriaErrors?: CriteriaIssue[]
  /** Warnings / prompts from the checks — the tiles ran. */
  criteriaNotices?: CriteriaIssue[]
}

/** Fetch every tile's dataset for a dashboard (per-tile failure isolated). */
export async function fetchDashboardData(body: {
  project: string
  obj_name?: string
  inline_model?: unknown
  global_criteria?: Record<string, unknown>
  /** Build shared in-memory models: one query per source instead of per tile. */
  in_memory?: boolean
}): Promise<DashboardDataResult> {
  const res = await api.post('/visual-data/dashboard', body)
  return res.data as DashboardDataResult
}

export interface DiagnoseResult {
  success: boolean
  mode: string
  tables: Array<{ table: string; rows?: number; error?: string }>
  afterJoinRows: { rows?: number; error?: string }
  resultRows: { rows?: number; error?: string } | null
  sample: Array<Record<string, unknown>>
  needsInput: boolean
  missingParams: string[]
}

/** Diagnose a 0-row visual: per-table / after-join / final row counts. */
export async function diagnoseVisual(body: VisualDataRequest): Promise<DiagnoseResult> {
  const res = await api.post('/visual-data/diagnose', body)
  return res.data as DiagnoseResult
}

export interface PreviewSqlResult {
  success: boolean
  sourceSql: string
  params: Record<string, unknown>
  promptVars: Array<{ name: string; label: string; data_type: string; operator: string; default: unknown }>
}

// ── Criteria lookup + item_change (parity with the transaction designer) ────

/** F2 reference-picker rows for a visual/dashboard criterion. */
export async function criteriaLookup(body: {
  project: string
  lookup_spec?: string
  search?: string
  limit?: number
  filter_sql?: string
  form_values?: Record<string, unknown>
  header_values?: Record<string, unknown>
  literal_values?: Record<string, unknown>
  lookup_sql?: string
  lookup_sql_data_col?: string
}): Promise<LookupResponse> {
  const res = await api.post('/visual-data/criteria-lookup', body)
  return res.data as LookupResponse
}

/** Run a criterion's on-change item_change rule (fills/locks other criteria). */
export async function criteriaItemChange(body: {
  project: string
  kind?: 'visual' | 'dashboard' | 'report'
  obj_name?: string
  inline_model?: unknown
  trigger_param: string
  params?: Record<string, unknown>
}): Promise<ItemChangeResult> {
  const res = await api.post('/visual-data/criteria-item-change', body)
  return res.data as ItemChangeResult
}

/**
 * Build a `VisualCriteriaRuntime` for the in-app surfaces (designer live
 * preview + Run) — both hit the same `/api/visual-data` endpoints. The
 * standalone downloaded app supplies its own (see runtime/client).
 */
export function makeVisualCriteriaRuntime(opts: {
  project: string
  kind?: 'visual' | 'dashboard' | 'report'
  obj_name?: string
  inline_model?: unknown
  notify?: TxnNotifier
}): VisualCriteriaRuntime {
  return {
    lookup: (project, _objName, lookup, search, limit, o) =>
      criteriaLookup({
        project,
        lookup_spec: lookup,
        search,
        limit,
        filter_sql: o?.filter,
        form_values: o?.formValues,
        header_values: o?.headerValues,
        literal_values: o?.literalValues,
        lookup_sql: o?.lookupSql,
        lookup_sql_data_col: o?.lookupSqlDataCol,
      }),
    criteriaItemChange: (triggerParam, params) =>
      criteriaItemChange({
        project: opts.project,
        kind: opts.kind,
        obj_name: opts.obj_name,
        inline_model: opts.inline_model,
        trigger_param: triggerParam,
        params,
      }),
    notify: opts.notify,
  }
}

export interface SqlColumnsResult {
  success: boolean
  columns: Array<{ name: string; type: string; coltype: string }>
  sourceSql: string
}

/**
 * Ask the database what a SQL-mode visual's SOURCE_SQL actually returns.
 * The designer uses this to expand `SELECT *` into real columns — the
 * client-side SELECT-list parse cannot. Runs read-only, capped to one row.
 */
/** One dialect-supported function for the designer's pickers. */
export interface VisualFunction {
  name: string
  label: string
  kind: 'aggregate' | 'statistical' | 'window'
}

/** The aggregate/statistical/window functions the visual's bound database
 *  supports — resolved server-side from the SAME connection execution uses,
 *  so the designer never offers a function the database cannot run. */
export async function fetchVisualFunctions(body: {
  project: string
  model: unknown
}): Promise<{ dialect: string; supportsWindow: boolean; functions: VisualFunction[] }> {
  const res = await api.post('/visual-data/functions', body)
  return res.data as { dialect: string; supportsWindow: boolean; functions: VisualFunction[] }
}

export async function fetchSqlColumns(body: {
  project: string
  model: unknown
  params?: Record<string, unknown>
}): Promise<SqlColumnsResult> {
  const res = await api.post('/visual-data/sql-columns', body)
  return res.data as SqlColumnsResult
}

/** Return ONLY the generated SQL for a model (dev aid; does not execute). */
export async function fetchVisualSql(body: {
  project: string
  model?: unknown
  obj_name?: string
  params?: Record<string, unknown>
}): Promise<PreviewSqlResult> {
  const res = await api.post('/visual-data/preview-sql', body)
  return res.data as PreviewSqlResult
}
